"use client";

import { useEffect } from "react";
import { sendLandingEvent as sendEvent } from "../../lib/analytics";
import { appendMetaAttributionParams } from "./metaAttribution";
import { appendRedditAttributionParams } from "./redditAttribution";
import {
  appendPromoteKitAttributionParams,
  initPromoteKitAttribution,
} from "./promotekitAttribution";

const APP_HOSTS = new Set([
  "app.instanttranscriber.com",
  "instanttranscriber.com",
]);

const META_LEAD_STORAGE_KEY = "instanttranscriber_meta_event:Lead:signup";
const REDDIT_LEAD_STORAGE_KEY = "instanttranscriber_reddit_event:Lead:signup";

const isMetaPixelEnabled = () => {
  return window.__instantTranscriberMarketingConsent !== false;
};

const trackMetaLead = (params = {}) => {
  if (typeof window === "undefined" || !isMetaPixelEnabled() || typeof window.fbq !== "function") return;

  try {
    if (window.sessionStorage?.getItem(META_LEAD_STORAGE_KEY)) return;
    window.sessionStorage?.setItem(META_LEAD_STORAGE_KEY, String(Date.now()));
  } catch {}

  window.fbq("track", "Lead", {
    content_name: "signup",
    source: "landing",
    ...params,
  });
};

const isRedditPixelEnabled = () => {
  return window.__instantTranscriberMarketingConsent !== false;
};

const trackRedditLead = () => {
  if (typeof window === "undefined" || !isRedditPixelEnabled() || typeof window.rdt !== "function") return;

  try {
    if (window.sessionStorage?.getItem(REDDIT_LEAD_STORAGE_KEY)) return;
    window.sessionStorage?.setItem(REDDIT_LEAD_STORAGE_KEY, String(Date.now()));
  } catch {}

  window.rdt("track", "Lead");
};

const getAppLinkEventName = (url) => {
  if (url.hostname === "app.instanttranscriber.com" || (url.origin === window.location.origin && /^\/app(?:\/|$)/.test(url.pathname))) {
    if (url.pathname.includes("/signup")) return "landing_signup_click";
    if (url.pathname.includes("/login")) return "landing_login_click";
    return "landing_app_click";
  }

  if (url.pathname.startsWith("/app")) return "landing_app_click";
  return null;
};

const getClickLocation = (element) => {
  if (element.closest("#try-transcription")) return "guest_entry";
  if (element.closest("header")) return "header";
  if (element.closest("footer")) return "footer";
  if (element.closest("#free-audio-tools")) return "free_audio_tools";
  if (element.closest("#pricing")) return "pricing";
  if (element.closest("#how-it-works")) return "how_it_works";
  if (element.closest("#get-started")) return "final_cta";
  if (element.closest("#hero-cta")) return "hero";
  return "body";
};

const getAuthTarget = (url) => {
  if (url.hostname !== "app.instanttranscriber.com" && !(url.origin === window.location.origin && /^\/app(?:\/|$)/.test(url.pathname))) return null;
  if (url.pathname.includes("/signup")) return "signup";
  if (url.pathname.includes("/login")) return "login";
  return null;
};

const getLandingLocale = () => {
  const firstSegment = window.location.pathname.split("/").filter(Boolean)[0];
  return ["de", "fr"].includes(firstSegment) ? firstSegment : "en";
};

const getCtaParams = (element, url, clickLocation, authTarget) => {
  const dataset = element?.dataset || {};
  return {
    cta_id: dataset.analyticsCtaId || undefined,
    cta_location: dataset.analyticsCtaLocation || clickLocation,
    auth_method: dataset.analyticsAuthMethod || undefined,
    auth_target: dataset.analyticsAuthTarget || authTarget || undefined,
    plan_intent: dataset.analyticsPlanIntent || undefined,
    billing_interval: dataset.analyticsBillingInterval || undefined,
    account_type: ['visitor','guest','registered'].includes(dataset.analyticsAccountType) ? dataset.analyticsAccountType : undefined,
    offer_variant: ['landing_hero','landing_pricing'].includes(dataset.analyticsOfferVariant) ? dataset.analyticsOfferVariant : undefined,
    desktop_platform: ['macos','windows','linux-deb','linux-appimage'].includes(dataset.analyticsDesktopPlatform) ? dataset.analyticsDesktopPlatform : undefined,
    desktop_version: /^\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(dataset.analyticsDesktopVersion || '') ? dataset.analyticsDesktopVersion : undefined,
    destination: dataset.analyticsDestination || url.pathname || url.hash || undefined,
    landing_path: window.location.pathname,
    landing_locale: getLandingLocale(),
  };
};

const addLandingAttribution = (anchor, url, clickLocation, authTarget, ctaParams = {}) => {
  const nextUrl = appendRedditAttributionParams(
    appendPromoteKitAttributionParams(
      appendMetaAttributionParams(window.instantTranscriberAcquisition?.decorateUrl(url) || new URL(url.toString())),
    ),
  );

  if (!authTarget) {
    anchor.href = nextUrl.toString();
    return nextUrl;
  }

  nextUrl.searchParams.set("from", "landing");
  nextUrl.searchParams.set("landing_path", window.location.pathname);
  nextUrl.searchParams.set("landing_locale", ctaParams.landing_locale || getLandingLocale());
  nextUrl.searchParams.set("cta_location", ctaParams.cta_location || clickLocation);
  nextUrl.searchParams.set("auth_target", authTarget);
  if (ctaParams.cta_id) nextUrl.searchParams.set("cta_id", ctaParams.cta_id);
  if (ctaParams.auth_method) nextUrl.searchParams.set("auth_method", ctaParams.auth_method);
  if (ctaParams.plan_intent) nextUrl.searchParams.set("plan_intent", ctaParams.plan_intent);
  if (ctaParams.billing_interval) nextUrl.searchParams.set("billing_interval", ctaParams.billing_interval);
  anchor.href = nextUrl.toString();
  return nextUrl;
};

export default function AnalyticsEvents() {
  useEffect(() => {
    initPromoteKitAttribution();

    const handleCustomTrack = (event) => {
      const detail = event.detail || {};
      if (!detail.event) return;
      sendEvent(detail.event, detail.params || {});

      if (detail.event === "landing_auth_click" && detail.params?.auth_target === "signup") {
        trackMetaLead({
          method: detail.params.auth_method,
          cta_location: detail.params.cta_location || detail.params.click_location,
        });
        trackRedditLead();
      }
    };

    const handleClick = (event) => {
      const anchor = event.target?.closest?.("a[href]");
      if (!anchor) return;

      let url;
      try {
        url = new URL(anchor.href, window.location.origin);
      } catch {
        return;
      }

      const clickLocation = getClickLocation(anchor);
      const authTarget = getAuthTarget(url);
      const ctaParams = getCtaParams(anchor, url, clickLocation, authTarget);
      const customEventName = anchor.dataset?.analyticsEvent;
      const shouldTrackCustomCta = Boolean(customEventName || ctaParams.cta_id);

      if (shouldTrackCustomCta) {
        sendEvent(customEventName || "landing_cta_click", {
          ...ctaParams,
          link_hostname: url.hostname,
          link_path: `${url.pathname}${url.hash || ""}`,
          click_location: ctaParams.cta_location || clickLocation,
        });

        if (customEventName !== "landing_cta_click") {
          sendEvent("landing_cta_click", {
            ...ctaParams,
            link_hostname: url.hostname,
            link_path: `${url.pathname}${url.hash || ""}`,
            click_location: ctaParams.cta_location || clickLocation,
          });
        }
      }

      if (!APP_HOSTS.has(url.hostname) && url.origin !== window.location.origin) return;
      const eventName = getAppLinkEventName(url);
      if (!eventName) return;
      const trackedUrl = addLandingAttribution(anchor, url, clickLocation, authTarget, ctaParams);

      sendEvent(eventName, {
        ...ctaParams,
        link_hostname: trackedUrl.hostname,
        link_path: trackedUrl.pathname,
        click_location: ctaParams.cta_location || clickLocation,
      });

      if (authTarget) {
        if (authTarget === "signup") {
          trackMetaLead({
            method: ctaParams.auth_method || "link",
            cta_location: ctaParams.cta_location || clickLocation,
          });
          trackRedditLead();
        }

        sendEvent("landing_auth_click", {
          ...ctaParams,
          auth_target: authTarget,
          link_hostname: trackedUrl.hostname,
          link_path: trackedUrl.pathname,
          click_location: ctaParams.cta_location || clickLocation,
        });
      }
    };

    window.addEventListener("track", handleCustomTrack);
    document.addEventListener("click", handleClick, true);

    return () => {
      window.removeEventListener("track", handleCustomTrack);
      document.removeEventListener("click", handleClick, true);
    };
  }, []);

  return null;
}
